import { useState } from "react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown, Filter, Search } from "lucide-react";

import { DebouncedInput } from "@/components/common";
import { Button } from "./button/button";

// Types for the table component
export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey?: string;
  searchPlaceholder?: string;
  showPagination?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  pageSize?: number;
  className?: string;
  onRowClick?: (row: Row<TData>) => void;
  loading?: boolean;
  emptyMessage?: string;
}

// Sort indicator component
const SortIndicator = ({ isSorted }: { isSorted: false | "asc" | "desc" }) => {
  if (isSorted === false) return <ChevronsUpDown className="h-4 w-4" />;
  if (isSorted === "asc") return <ChevronUp className="h-4 w-4" />;
  return <ChevronDown className="h-4 w-4" />;
};

// Loading skeleton row
const LoadingRow = ({ columns }: { columns: number }) => (
  <tr className="animate-pulse">
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} className="px-6 py-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </td>
    ))}
  </tr>
);

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = "Buscar...",
  showPagination = true,
  showSearch = true,
  showFilters = true,
  pageSize = 10,
  className = "",
  onRowClick,
  loading = false,
  emptyMessage = "No hay datos disponibles",
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  const handleRowClick = (row: Row<TData>) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Search and Filters Bar */}
      {(showSearch || showFilters) && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6 p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark">
          {showSearch && searchKey && (
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <DebouncedInput
                  placeholder={searchPlaceholder}
                  value={globalFilter}
                  onChange={setGlobalFilter}
                  className="pl-10"
                  debounce={500}
                />
              </div>
            </div>
          )}

          {showFilters && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Reset all filters
                  table.resetColumnFilters();
                  setGlobalFilter("");
                }}
              >
                <Filter className="h-4 w-4" />
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Table Container */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden shadow-soft">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-border-light dark:border-border-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-6 py-3 text-left text-xs font-medium text-text-light-secondary dark:text-text-dark-secondary uppercase tracking-wider ${
                        header.column.getCanSort() ? "cursor-pointer select-none" : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <SortIndicator isSorted={header.column.getIsSorted()} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-border-light dark:divide-border-dark">
              {loading ? (
                // Loading state
                Array.from({ length: pageSize }).map((_, index) => (
                  <LoadingRow key={index} columns={columns.length} />
                ))
              ) : table.getRowModel().rows.length ? (
                // Data rows
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      onRowClick ? "cursor-pointer" : ""
                    }`}
                    onClick={() => handleRowClick(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-6 py-4 whitespace-nowrap text-sm text-text-light-primary dark:text-text-dark-primary"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                // Empty state
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-12 text-center text-text-light-tertiary dark:text-text-dark-tertiary"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <Search className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium">{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {showPagination && table.getPageCount() > 1 && (
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-border-light dark:border-border-dark">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-text-light-secondary dark:text-text-dark-secondary">
                <span>
                  Página {table.getState().pagination.pageIndex + 1} de{" "}
                  {table.getPageCount()}
                </span>
                <span>•</span>
                <span>{table.getFilteredRowModel().rows.length} resultados</span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Anterior
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({
                    length: Math.min(5, table.getPageCount()),
                  }).map((_, index) => {
                    const pageIndex = table.getState().pagination.pageIndex;
                    const pageCount = table.getPageCount();
                    let pageNumber: number;

                    if (pageCount <= 5) {
                      pageNumber = index;
                    } else if (pageIndex < 3) {
                      pageNumber = index;
                    } else if (pageIndex >= pageCount - 3) {
                      pageNumber = pageCount - 5 + index;
                    } else {
                      pageNumber = pageIndex - 2 + index;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={pageIndex === pageNumber ? "default" : "outline"}
                        size="sm"
                        onClick={() => table.setPageIndex(pageNumber)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNumber + 1}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
